import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const LANGS = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'bg', label: 'BG', name: 'Български' },
  { code: 'ru', label: 'RU', name: 'Русский' },
  { code: 'it', label: 'IT', name: 'Italiano' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  const navItems = [
    { label: t('nav.home'), href: '#home' },
    { label: t('nav.about'), href: '#about' },
    { label: t('nav.video'), href: '#video' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.contracts'), href: '#contracts' },
    { label: t('nav.profit'), href: '#profit' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (href: string) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setIsLangOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#050508]/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <button onClick={() => scrollToSection('#home')} className="flex items-center gap-2.5 cursor-pointer flex-shrink-0">
              <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
                <i className="ri-rocket-fill text-black text-lg md:text-xl"></i>
              </div>
              <div>
                <div className="font-['Orbitron'] text-white font-black text-base md:text-lg tracking-wider leading-none">SPACEX</div>
                <div className="text-amber-400 text-[9px] font-['Rajdhani'] uppercase tracking-[0.2em]">IPO 2025</div>
              </div>
            </button>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-0.5">
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="px-3 py-2 text-gray-300 hover:text-amber-400 text-sm font-['Rajdhani'] uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap"
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Right side: Lang + CTA */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Language Switcher */}
              <div ref={langRef} className="relative">
                <button
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  className="flex items-center gap-1.5 px-3 py-2 border border-white/10 hover:border-amber-500/40 rounded-lg text-gray-300 hover:text-amber-400 text-xs font-['Rajdhani'] uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap"
                >
                  <span>{currentLang.label}</span>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${isLangOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isLangOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-[#0e0e1a] border border-white/10 rounded-xl overflow-hidden min-w-[130px] shadow-xl">
                    {LANGS.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLang(lang.code)}
                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-['Rajdhani'] uppercase tracking-wider transition-colors cursor-pointer ${
                          i18n.language === lang.code
                            ? 'text-amber-400 bg-amber-500/10'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <span className="font-bold">{lang.label}</span>
                        <span className="text-gray-500 text-[10px] normal-case tracking-normal">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* CTA – desktop only */}
              <button
                onClick={() => scrollToSection('#contracts')}
                className="hidden md:block bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-black text-xs md:text-sm px-4 md:px-6 py-2.5 md:py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap font-['Orbitron'] tracking-wider"
              >
                {t('nav.invest')}
              </button>

              {/* Hamburger */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-9 h-9 flex items-center justify-center text-white cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-4-line'} text-xl`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
        <div
          className={`absolute top-16 left-3 right-3 bg-[#0e0e1a] border border-white/10 rounded-2xl p-5 transition-all duration-300 ${
            isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0'
          }`}
        >
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollToSection(item.href)}
                className="w-full text-left px-4 py-3 text-gray-300 hover:text-amber-400 hover:bg-white/5 rounded-xl text-sm font-['Rajdhani'] uppercase tracking-wider transition-all cursor-pointer"
              >
                {item.label}
              </button>
            ))}
            <div className="mt-3 pt-3 border-t border-white/10">
              <button
                onClick={() => scrollToSection('#contracts')}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-black font-black text-sm px-6 py-3.5 rounded-xl font-['Orbitron'] tracking-wider cursor-pointer whitespace-nowrap"
              >
                {t('nav.invest')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}